import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Paper,
  Tooltip,
  Link,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import BugReportIcon from '@mui/icons-material/BugReport';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloseIcon from '@mui/icons-material/Close';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from '../../../utils/languages';
import ChatMessage, { AssistantAvatar, CHAT_COLORS } from './ChatMessage';
import SuggestionChips from './SuggestionChips';

const INITIAL_SUGGESTIONS = {
  fi: [
    'Miten luon työmääräyksen?',
    'Miten laskutan työmääräyksen?',
    'Miten tuon osia työmääräykselle?',
  ],
  en: [
    'How do I create a work order?',
    'How do I invoice a work order?',
    'How do I import parts?',
  ],
  sv: [
    'Hur skapar jag en arbetsorder?',
    'Hur fakturerar jag en arbetsorder?',
    'Hur lägger jag till delar på en arbetsorder?',
  ],
};

const TEST_IDS = {
  floating: 'help-chat-panel',
  large: 'help-chat-large',
  embedded: 'help-chat-embedded',
};

/**
 * What the support request form opens with when the user escalates from a chat message: the
 * model's own draft when the docs did not answer, otherwise the question itself, plus the
 * conversation so far so support sees what the bot said.
 */
function escalationPrefill(messages, index) {
  const message = messages[index];
  const lastQuestion = [...messages.slice(0, index)].reverse().find((m) => m.role === 'user');
  return {
    requestType: 'help',
    description: message.supportRequest || lastQuestion?.content || '',
    chatMessages: messages
      .slice(0, index + 1)
      .filter((m) => !m.isError)
      .map(({ role, content }) => ({ role, content })),
  };
}

const headerIconSx = (dark) => ({ color: dark ? 'rgba(255,255,255,0.8)' : 'text.secondary' });

/**
 * Verstaani avustaja, in one of three places:
 * - floating: the corner panel above the FAB (navy header, minimize, expand)
 * - large: inside ChatLargeDialog (light header, shrink back to the corner, close)
 * - embedded: inside the support page card, which already has the title (slim toolbar only)
 * The conversation itself looks the same everywhere.
 */
function ChatPanel({
  messages,
  loading,
  onSendMessage,
  onSuggestionClick,
  onNewChat,
  onMinimize,
  onExpand,
  onClose,
  onOpenSupport,
  onOpenVideos,
  onOpenDismiss,
  variant = 'floating',
}) {
  const { t, i18n } = useTranslation();
  const messagesEndRef = useRef(null);
  const [input, setInput] = useState('');
  const floating = variant === 'floating';

  const initialSuggestions = INITIAL_SUGGESTIONS[normalizeLanguage(i18n.language)] ?? INITIAL_SUGGESTIONS.fi;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSend = () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    onSendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Find the last assistant message to show its suggestions
  const lastAssistantMsg = [...messages].reverse().find((m) => m.role === 'assistant');

  const toolbarButtons = (dark) => (
    <>
      <Tooltip title={t('helpChatbot.newChat')}>
        <IconButton size="small" aria-label={t('helpChatbot.newChat')} sx={headerIconSx(dark)} onClick={onNewChat}>
          <AddCommentOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t('Help videos')}>
        <IconButton size="small" aria-label={t('Help videos')} sx={headerIconSx(dark)} onClick={onOpenVideos}>
          <OndemandVideoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </>
  );

  let header = null;
  if (floating) {
    header = (
      <Box
        sx={{
          bgcolor: '#1a365d',
          color: '#fff',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <SmartToyOutlinedIcon sx={{ fontSize: 24 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 600, lineHeight: 1.2 }}>
            {t('helpChatbot.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>
            {t('helpChatbot.subtitle')}
          </Typography>
        </Box>
        {toolbarButtons(true)}
        <Tooltip title={t('Submit support request')}>
          <IconButton
            size="small"
            aria-label={t('Submit support request')}
            sx={headerIconSx(true)}
            onClick={() => onOpenSupport()}
          >
            <BugReportIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {onExpand && (
          <Tooltip title={t('helpChatbot.openLarge')}>
            <IconButton size="small" aria-label={t('helpChatbot.openLarge')} sx={headerIconSx(true)} onClick={onExpand}>
              <OpenInFullIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('helpChatbot.minimize')}>
          <IconButton size="small" aria-label={t('helpChatbot.minimize')} sx={headerIconSx(true)} onClick={onMinimize}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  } else if (variant === 'large') {
    header = (
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
        }}
      >
        <AssistantAvatar size={36} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {t('helpChatbot.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
            {t('helpChatbot.subtitle')}
          </Typography>
        </Box>
        {toolbarButtons(false)}
        {onMinimize && (
          <Tooltip title={t('helpChatbot.shrink')}>
            <IconButton size="small" aria-label={t('helpChatbot.shrink')} sx={headerIconSx(false)} onClick={onMinimize}>
              <CloseFullscreenIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={t('Close')}>
          <IconButton size="small" aria-label={t('Close')} sx={headerIconSx(false)} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  } else {
    // Narrow phones: tighten the text so both buttons still fit on one row
    const embeddedButtonSx = {
      color: 'text.secondary',
      whiteSpace: 'nowrap',
      '@media (max-width: 400px)': {
        fontSize: '0.75rem',
        minWidth: 0,
        px: 0.75,
        '& .MuiButton-startIcon': { mr: 0.5 },
      },
    };
    header = (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: 0.5,
          px: 1,
          pt: 0.5,
          flexShrink: 0,
          '@media (max-width: 400px)': { px: 0, gap: 0 },
        }}
      >
        <Button size="small" startIcon={<AddCommentOutlinedIcon />} onClick={onNewChat} sx={embeddedButtonSx}>
          {t('helpChatbot.newChat')}
        </Button>
        <Button size="small" startIcon={<OndemandVideoIcon />} onClick={onOpenVideos} sx={embeddedButtonSx}>
          {t('Help videos')}
        </Button>
      </Box>
    );
  }

  return (
    <Paper
      elevation={floating ? 8 : 0}
      data-testid={TEST_IDS[variant] ?? TEST_IDS.floating}
      sx={floating ? {
        position: 'fixed',
        bottom: 88,
        right: 24,
        width: 420,
        maxHeight: 'min(75vh, calc(100vh - 120px))',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px',
        overflow: 'hidden',
        zIndex: 1001,
        // Mobile: full width
        '@media (max-width: 600px)': {
          width: 'calc(100vw - 16px)',
          right: 8,
          bottom: 80,
          maxHeight: 'calc(100vh - 100px)',
        },
      } : {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: variant === 'embedded' ? '12px' : 0,
        overflow: 'hidden',
        border: variant === 'embedded' ? '1px solid #e2e8f0' : 'none',
        bgcolor: '#fff',
      }}
    >
      {header}

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: '#fff',
          minHeight: 0,
        }}
      >
        {/* Welcome message (always shown at top) */}
        <ChatMessage role="assistant" content={t('helpChatbot.welcome')} />

        {/* Initial suggestions when no messages yet */}
        {messages.length === 0 && (
          <SuggestionChips
            suggestions={initialSuggestions}
            onSuggestionClick={onSuggestionClick}
          />
        )}

        {/* Conversation messages */}
        {messages.map((msg, index) => (
          <React.Fragment key={index}>
            <ChatMessage role={msg.role} content={msg.content} />
            {/* The docs did not answer (or the call failed): one click to customer service,
                the form prefilled and the chat attached */}
            {msg.role === 'assistant' && (msg.supportRequest || msg.isError) && (
              <Box sx={{ ml: 4.5, mb: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<SupportAgentOutlinedIcon />}
                  data-testid="help-chat-escalate"
                  onClick={() => onOpenSupport(escalationPrefill(messages, index))}
                  sx={{ borderRadius: '999px', textTransform: 'none' }}
                >
                  {t('Submit support request')}
                </Button>
              </Box>
            )}
            {/* Show suggestions after the last assistant message */}
            {msg.role === 'assistant' &&
              msg === lastAssistantMsg &&
              msg.suggestions &&
              msg.suggestions.length > 0 &&
              !loading && (
                <SuggestionChips
                  suggestions={msg.suggestions}
                  onSuggestionClick={onSuggestionClick}
                />
              )}
          </React.Fragment>
        ))}

        {/* Typing indicator */}
        {loading && <ChatMessage role="assistant" isLoading />}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box
        sx={{
          p: 1.5,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#fff',
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
          flexShrink: 0,
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={3}
          size="small"
          placeholder={t('helpChatbot.inputPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          inputProps={{ maxLength: 2000 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              fontSize: '0.875rem',
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || loading}
          aria-label={t('Submit')}
          sx={{
            bgcolor: CHAT_COLORS.userBubble,
            color: '#fff',
            width: 36,
            height: 36,
            '&:hover': { bgcolor: '#2b6cb0' },
            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: CHAT_COLORS.muted },
          }}
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Footer disclaimer */}
      <Box
        sx={{
          px: 2,
          pb: 1,
          pt: 0.5,
          bgcolor: '#fff',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: CHAT_COLORS.muted, fontSize: '0.7rem' }}>
          {t('helpChatbot.disclaimer')}
          {' · '}
          <Link
            component="button"
            variant="caption"
            onClick={() => onOpenSupport()}
            sx={{ color: CHAT_COLORS.muted, fontSize: '0.7rem', verticalAlign: 'baseline' }}
          >
            {t('helpChatbot.contactSupport')}
          </Link>
          {floating && (
            <>
              {' · '}
              <Link
                component="button"
                variant="caption"
                onClick={onOpenDismiss}
                sx={{ color: CHAT_COLORS.muted, fontSize: '0.7rem', verticalAlign: 'baseline' }}
              >
                {t('Dismiss widget')}
              </Link>
            </>
          )}
        </Typography>
      </Box>
    </Paper>
  );
}

export default ChatPanel;
